
import React, { useState, useMemo } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, Button, Typography } from '@mui/material';

const ImpactedAreaLightning = ({ loading, tableData, dateHrs }) => {
  const [activeFilter, setActiveFilter] = useState('All');

  const legendColors = useMemo(() => ({
    'Low': 'rgb(83, 133, 25)',
    'Moderate': 'rgb(243, 235, 18)',
    'High': 'rgb(243, 155, 58)',
    'Very High': 'rgb(230, 45, 58)',
    'N/A': 'rgb(222, 222, 223)',
    'All': '#f0f0f0',
  }), []);

  const combinedFilteredData = useMemo(() => {
    let currentData = tableData || [];

    if (activeFilter !== 'All') {
      currentData = currentData.filter(item =>
        dateHrs.some(dateHr => {
          const hr = dateHr.split('_')[1];
          return item[`Advisory_${hr}`] === activeFilter;
        })
      );
    }

    return currentData.sort((a, b) => a.SlNo - b.SlNo);
  }, [tableData, activeFilter, dateHrs]);

  const columns = useMemo(() => {
    const getCharacteristicColor = (advisory) => legendColors[advisory] || legendColors['N/A'];

    return [
      {
        accessorKey: 'SlNo',
        header: 'S.No',
        size: 50,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: 'Municipality',
        header: 'Municipality',
        size: 150,
      },
      {
        accessorKey: 'PostAdmin',
        header: 'Post Administrative',
        size: 150,
      },
      ...dateHrs.map(dateHr => {
        const hr = dateHr.split('_')[1];
        return {
          accessorKey: `Advisory_${hr}`,
          header: `Advisory (${dateHr.replace('_', ' ')})`,
          size: 120,
          Cell: ({ cell }) => (
            <span
              style={{
                backgroundColor: getCharacteristicColor(cell.getValue()),
                padding: '2px 5px',
                borderRadius: '3px',
                display: 'inline-block',
                color: 'black',
                fontWeight: 'bold',
                textAlign: 'center',
                minWidth: '80px',
              }}
            >
              {cell.getValue() || 'N/A'}
            </span>
          ),
        };
      }),
    ];
  }, [dateHrs, legendColors]);

  const filterButtons = useMemo(() => {
    const riskLevels = ['All', 'Low', 'Moderate', 'High', 'Very High'];
    return riskLevels.map(level => (
      <Button
        key={level}
        onClick={() => setActiveFilter(level)}
        sx={{
          minWidth: '80px',
          padding: '6px 12px',
          borderRadius: '20px',
          fontWeight: 'bold',
          fontSize: '14px',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          textTransform: 'none',
          ml: 1,
          mr: 1,
          backgroundColor: legendColors[level],
          color: 'black',
          border: activeFilter === level ? '2px solid black' : '2px solid transparent',
          boxShadow: activeFilter === level ? '0 0 0 2px black' : 'none',
          '&:hover': {
            backgroundColor: legendColors[level],
            opacity: 0.8,
            transform: 'translateY(-2px)',
          },
          '&.Mui-focusVisible': {
            outline: '2px solid black',
            outlineOffset: '2px',
          },
        }}
      >
        {level}
      </Button>
    ));
  }, [activeFilter, legendColors]);

  return (
    <div className="info-content-text">
      <Box sx={{ p: 2, borderTop: '1px solid #ddd', overflow: 'auto', width: '100%', bgcolor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <MaterialReactTable
          columns={columns}
          data={combinedFilteredData}
          enableColumnFilters={false}
          enableColumnActions={false}
          enableSorting
          enablePagination
          paginationDisplayMode='pages'
          enableDensityToggle={true}
          enableFullScreenToggle={true}
          enableGlobalFilter={false}
          initialState={{
            columnVisibility: { SlNo: true },
            density: 'compact',
            pagination: { pageSize: 10 },
          }}
          renderTopToolbarCustomActions={() => (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                width: '100%',
                p: '0.5rem',
                gap: '1rem',
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Impacted Area
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                {filterButtons}
              </Box>
            </Box>
          )}
          muiTableContainerProps={{ sx: { maxHeight: '600px' } }}
          muiTableHeadCellProps={{
            sx: {
              fontWeight: 'bold',
              backgroundColor: '#e0e0e0',
              color: '#333',
              borderBottom: '1px solid #ddd',
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontSize: '14px',
              color: '#333',
              borderBottom: '1px solid #ddd',
            },
          }}
          renderEmptyRowsFallback={() => (
            <Typography sx={{ p: 2, textAlign: 'center', color: '#666' }}>
              {loading ? 'Loading data...' : 'No impact data available.'}
            </Typography>
          )}
        />
      </Box>
    </div>
  );
};

export default ImpactedAreaLightning;
